import { memo, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { Button, Col, PageSection, Panel, ResponsiveImage, Row, SubHeading, Text } from 'reaxl';
import { DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';
import { preorderInventoryImpressions } from 'reaxl-analytics-handlers';
import { EmailCTA } from 'reaxl-email';
import { InventoryListingBody } from 'reaxl-listing';

import { placeholderImages } from 'axl-config';

import { getPriceLabel } from 'wallet-manager';

import { buildAndPriceClick } from '@/analytics/bap/buildAndPriceEvents';

import bapActiveModelDuck from '@/ducks/bap/bapActiveModelDuck.js';
import kbbVRSDataDuck from '@/ducks/kbbVRSDataDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import preorderDuck from '@/ducks/preorderDuck.js';
import vdpActiveEmailListingDuck from '@/ducks/vdp/vdpActiveEmailListingDuck.js';
import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import useBapNavigation from '@/hooks/useBapNavigation';

// Analytics
// Utilities

function PreorderSearchContainer() {
    const dispatch = useDispatch();
    const device = useDevice();
    const notXS = _get(device, 'greaterThan.xs', false);
    const orientation = ['sm', 'md'].includes(device.mediaType) ? 'landscape' : device.orientation;

    const navigateToBap = useBapNavigation();

    const {
        my_wallet: [enableMyWallet],
        preorder: [enablePreorder, {
            build_price_text: buildAndPriceButtonText,
        }],
    } = useFeatures([
        'my_wallet',
        'preorder',
    ]);

    const showListingWalletPayment = useSelector((state) => paymentsDuck.selectors.getMyWalletInteraction(state, 'paymentEmphasis'));
    const preorderListings = useSelector(preorderDuck.selectors.getPreorder);
    const vrsData = useSelector(kbbVRSDataDuck.selectors.getData);
    const vdpListing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const makeCodeList = vdpListing?.makeCode;
    const modelCodeList = vdpListing?.modelCode;
    const dealerId = vdpListing?.owner?.id;
    const zip = vdpListing?.owner?.location?.address?.zip;

    const hasMakeAndModel = makeCodeList && modelCodeList;

    const showPreorderCard = enablePreorder && hasMakeAndModel;

    const preorderCtaText = enablePreorder ? buildAndPriceButtonText : 'Build & Price';

    const preorderListing = preorderListings.length > 0 && preorderListings[0];

    const hasBapData = !_isEmpty(vrsData) && dealerId;
    const setActiveModel = useCallback((keys) => {
        dispatch(bapActiveModelDuck.creators.setActiveModel(keys));
    }, [dispatch]);

    const handleRequestAQuoteClick = useCallback((listing) => async () => {
        dispatch(vdpActiveEmailListingDuck.creators.setActiveResults([listing.id]));
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            emailCTAContext: 'quote',
            emailSubject: 'New Car Custom Order Inquiry',
            ownerId: '',
            showEmailModal: true,
        }));
    }, [dispatch]);

    const handleBuildClick = useCallback((event) => {
        sendClick(buildAndPriceClick, event, {
            par: 'v_lstg',
            inventoryId: preorderListing?.id,
            ownerId: preorderListing?.owner?.id,
        });

        setActiveModel(preorderListing?.modelCode || '');

        navigateToBap({
            makeCode: preorderListing?.makeCode,
            modelCode: preorderListing?.modelCode,
            ownerId: preorderListing?.owner?.id,
            year: preorderListing?.year,
            zip: vdpListing?.owner?.location?.address?.zip,
        });
    }, [navigateToBap, preorderListing?.id, preorderListing?.makeCode, preorderListing?.modelCode, preorderListing?.owner?.id, preorderListing?.year,
        setActiveModel, vdpListing?.owner?.location?.address?.zip]);

    const handleNewCarWalletClick = useCallback((listing = {}) => async (event) => {
        event.stopPropagation();

        dispatch(paymentsDuck.creators.updateMyWallet({
            listingId: listing.id,
            showPaymentDetailsModal: true,
        }));
    }, [dispatch]);

    // Determine price label based on vehicle make and type
    // For preorder listing, type is always 'New'
    const priceLabel = getPriceLabel({ makeCode: preorderListing?.makeCode, type: 'New' });

    const getListingProps = (listing) => ({
        listing: { ...listing },
        isPhoneNumberCallable: !notXS,
        showListingPaymentRibbon: !!enableMyWallet && !!showListingWalletPayment,
        onMyWalletClick: handleNewCarWalletClick(listing),
        orientation,
        uiContext: 'preorder',
        priceLabel,
    });

    const handleInViewImpression = useCallback((listing) => {
        sendImpressions({
            data: {
                inventoryId: listing.id,
            },
            name: preorderInventoryImpressions,
        });
    }, []);

    useEffect(() => {
        if (enablePreorder && vdpListing?.makeCode && vdpListing?.modelCode) {
            const makeModelPairs = [{
                makeCode: vdpListing?.makeCode,
                modelCode: vdpListing?.modelCode,
                makeModel: `${vdpListing?.makeCode}_${vdpListing.modelCode}`,
            }];
            dispatch(kbbVRSDataDuck.creators.loadData({ makeModelPairs }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (showPreorderCard && !_isEmpty(vrsData)) {
            const makeModelPairs = [{
                makeCode: makeCodeList,
                modelCode: modelCodeList,
            }];
            dispatch(preorderDuck.creators.loadData({
                dealerId,
                makeModelPairs,
                vrsData,
                year: vdpListing?.year,
                zip,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, showPreorderCard, vrsData]);

    // reset Build & Price active model
    useEffect(() => {
        setActiveModel('');
    }, [setActiveModel]);

    return showPreorderCard && preorderListing && (preorderListing?.owner?.id === vdpListing?.owner?.id) && (
        <PageSection
            alignment="left"
            heading="Close but not perfect? Order the car you want!"
            uiContext="level-3"
            className="container-fluid"
        >
            <SubHeading
                color="subdued"
                size={300}
                weight="normal"
                className="margin-bottom-4"
            >
                Work with this dealer to get the exact features and options you&apos;re looking for on your next vehicle.
            </SubHeading>
            <DelayedImpressionObserver
                onChange={() => handleInViewImpression(preorderListing)}
                key={`${preorderListing.id}-resources-delayed-impression`}
            >
                <Row>
                    <Col
                        md={6}
                        lg={4}
                        className="margin-bottom-sm-5 display-flex justify-content-center"
                    >
                        <ResponsiveImage
                            src={preorderListing.imageUrl || placeholderImages.noImageAvailable}
                            lazyload
                            alt={preorderListing.title}
                            width={preorderListing.imageUrl ? 751 : 250}
                            height={286}
                            className={clsx({ 'padding-4': !preorderListing.imageUrl })}
                        />
                    </Col>
                    <Col
                        md={6}
                        lg={8}
                    >
                        <Panel className="padding-vertical-4 padding-horizontal-4 margin-bottom-0">
                            <InventoryListingBody
                                className="padding-0"
                                key={preorderListing.id}
                                data-cmp="preorderCardBody"
                                uiContext="preorder"
                                {...getListingProps(preorderListing)}
                            />
                            <div className="text-center">
                                <div className="display-flex gap-3 flex-wrap justify-content-center margin-top-5 margin-bottom-2">
                                    <EmailCTA
                                        key="quote"
                                        buttonStyle="secondary"
                                        labelKey="quote"
                                        context="quote"
                                        onClick={handleRequestAQuoteClick(preorderListing)}
                                        listingId={preorderListing?.id}
                                        ownerId={preorderListing?.owner?.id}
                                        showIcon={false}
                                    />
                                    {hasBapData && (
                                        <Button
                                            bsStyle="primary"
                                            onClick={handleBuildClick}
                                        >
                                            {preorderCtaText}
                                        </Button>
                                    )}
                                </div>
                                <Text
                                    color="subdued-lightest"
                                    size={200}
                                >
                                    For illustration purposes only. May not match exact trim or color of the vehicle shown. No obligation to purchase.
                                </Text>
                            </div>
                        </Panel>
                    </Col>
                </Row>
            </DelayedImpressionObserver>
        </PageSection>
    );
}

export default memo(PreorderSearchContainer);
